import api from './axiosConfig';
import {
  SuperAdminDashboardData,
  TenantDto,
  CreateTenant,
  UpdateTenant,
  SubscriptionPlanDto,
  CreateSubscriptionPlan,
  UpdateSubscriptionPlan,
  InvoiceDto,
  GenerateInvoice,
  UpdateInvoice,
  SupportTicketDto,
  CreateSupportTicket,
  UpdateSupportTicket,
  UpdateTicketStatus,
  FeatureFlagDto,
  CreateFeatureFlag,
  UpdateFeatureFlag,
  FeatureDefinitionDto,
  TenantFeatureSettingsDto,
  SaveTenantFeatures,
  RoleTemplateDto,
  CreateRoleTemplate,
  UpdateRoleTemplate,
  PlatformAnalyticsDto,
  NotificationDto,
  CreateNotification,
  AuditLogDto,
  SecurityEventDto,
  UsageMonitoringDto,
  SystemSettingsDto,
  UpdateSystemSetting,
} from '../types/superAdminTypes';

const BASE = '/superadmin';

export const superAdminApi = {
  // ─── Dashboard ──────────────────────────────────────────────
  getDashboard: async (): Promise<SuperAdminDashboardData> => {
    const response = await api.get<SuperAdminDashboardData>(`${BASE}/dashboard`);
    return response.data;
  },

  // ─── Tenant Management ──────────────────────────────────────
  getTenants: async (params?: {
    searchTerm?: string;
    status?: number;
    planId?: string;
  }): Promise<TenantDto[]> => {
    const response = await api.get<TenantDto[]>(`${BASE}/tenants`, { params });
    return response.data;
  },

  getTenantById: async (id: string): Promise<TenantDto> => {
    const response = await api.get<TenantDto>(`${BASE}/tenants/${id}`);
    return response.data;
  },

  createTenant: async (data: CreateTenant): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/tenants`, data);
    return response.data;
  },

  updateTenant: async (id: string, data: UpdateTenant): Promise<void> => {
    await api.put(`${BASE}/tenants/${id}`, data);
  },

  activateTenant: async (id: string): Promise<void> => {
    await api.post(`${BASE}/tenants/${id}/activate`);
  },

  suspendTenant: async (id: string): Promise<void> => {
    await api.post(`${BASE}/tenants/${id}/suspend`);
  },

  deleteTenant: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/tenants/${id}`);
  },

  // ─── Subscription Plan Management ──────────────────────────
  getSubscriptionPlans: async (): Promise<SubscriptionPlanDto[]> => {
    const response = await api.get<SubscriptionPlanDto[]>(`${BASE}/subscription-plans`);
    return response.data;
  },

  getSubscriptionPlanById: async (id: string): Promise<SubscriptionPlanDto> => {
    const response = await api.get<SubscriptionPlanDto>(`${BASE}/subscription-plans/${id}`);
    return response.data;
  },

  createSubscriptionPlan: async (data: CreateSubscriptionPlan): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/subscription-plans`, data);
    return response.data;
  },

  updateSubscriptionPlan: async (id: string, data: UpdateSubscriptionPlan): Promise<void> => {
    await api.put(`${BASE}/subscription-plans/${id}`, data);
  },

  deleteSubscriptionPlan: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/subscription-plans/${id}`);
  },

  activateSubscriptionPlan: async (id: string): Promise<void> => {
    await api.post(`${BASE}/subscription-plans/${id}/activate`);
  },

  deactivateSubscriptionPlan: async (id: string): Promise<void> => {
    await api.post(`${BASE}/subscription-plans/${id}/deactivate`);
  },

  // ─── Billing & Invoice Management ──────────────────────────
  getInvoices: async (params?: {
    tenantId?: string;
    paymentStatus?: number;
    fromDate?: string;
    toDate?: string;
  }): Promise<InvoiceDto[]> => {
    const response = await api.get<InvoiceDto[]>(`${BASE}/invoices`, { params });
    return response.data;
  },

  getInvoiceById: async (id: string): Promise<InvoiceDto> => {
    const response = await api.get<InvoiceDto>(`${BASE}/invoices/${id}`);
    return response.data;
  },

  generateInvoice: async (data: GenerateInvoice): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/invoices`, data);
    return response.data;
  },

  updateInvoice: async (id: string, data: Partial<UpdateInvoice>): Promise<void> => {
    await api.put(`${BASE}/invoices/${id}`, data);
  },

  markInvoicePaid: async (id: string): Promise<void> => {
    await api.post(`${BASE}/invoices/${id}/mark-paid`);
  },

  // ─── Support Ticket Management ─────────────────────────────
  getSupportTickets: async (params?: {
    tenantId?: string;
    priority?: number;
    status?: number;
    searchTerm?: string;
  }): Promise<SupportTicketDto[]> => {
    const response = await api.get<SupportTicketDto[]>(`${BASE}/support-tickets`, { params });
    return response.data;
  },

  getSupportTicketById: async (id: string): Promise<SupportTicketDto> => {
    const response = await api.get<SupportTicketDto>(`${BASE}/support-tickets/${id}`);
    return response.data;
  },

  createSupportTicket: async (data: CreateSupportTicket): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/support-tickets`, data);
    return response.data;
  },

  updateSupportTicket: async (id: string, data: UpdateSupportTicket): Promise<void> => {
    await api.put(`${BASE}/support-tickets/${id}`, data);
  },

  updateTicketStatus: async (id: string, data: UpdateTicketStatus): Promise<void> => {
    await api.put(`${BASE}/support-tickets/${id}/status`, data);
  },

  // ─── Feature Flag Management ───────────────────────────────
  getFeatureFlags: async (): Promise<FeatureFlagDto[]> => {
    const response = await api.get<FeatureFlagDto[]>(`${BASE}/feature-flags`);
    return response.data;
  },

  createFeatureFlag: async (data: CreateFeatureFlag): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/feature-flags`, data);
    return response.data;
  },

  updateFeatureFlag: async (id: string, data: UpdateFeatureFlag): Promise<void> => {
    await api.put(`${BASE}/feature-flags/${id}`, data);
  },

  deleteFeatureFlag: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/feature-flags/${id}`);
  },

  // ─── Role Template Management ──────────────────────────────
  getFeatureDefinitions: async (): Promise<FeatureDefinitionDto[]> => {
    const response = await api.get<FeatureDefinitionDto[]>(`${BASE}/feature-definitions`);
    return response.data;
  },

  getTenantFeatures: async (tenantId: string): Promise<TenantFeatureSettingsDto> => {
    const response = await api.get<TenantFeatureSettingsDto>(`${BASE}/tenants/${tenantId}/features`);
    return response.data;
  },

  saveTenantFeatures: async (tenantId: string, data: SaveTenantFeatures): Promise<void> => {
    await api.put(`${BASE}/tenants/${tenantId}/features`, data);
  },

  getRoleTemplates: async (): Promise<RoleTemplateDto[]> => {
    const response = await api.get<RoleTemplateDto[]>(`${BASE}/role-templates`);
    return response.data;
  },

  getRoleTemplateById: async (id: string): Promise<RoleTemplateDto> => {
    const response = await api.get<RoleTemplateDto>(`${BASE}/role-templates/${id}`);
    return response.data;
  },

  createRoleTemplate: async (data: CreateRoleTemplate): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/role-templates`, data);
    return response.data;
  },

  updateRoleTemplate: async (id: string, data: UpdateRoleTemplate): Promise<void> => {
    await api.put(`${BASE}/role-templates/${id}`, data);
  },

  deleteRoleTemplate: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/role-templates/${id}`);
  },

  // ─── Platform Analytics ────────────────────────────────────
  getPlatformAnalytics: async (): Promise<PlatformAnalyticsDto> => {
    const response = await api.get<PlatformAnalyticsDto>(`${BASE}/analytics`);
    return response.data;
  },

  // ─── Notification Management ───────────────────────────────
  getNotifications: async (params?: {
    isSent?: boolean;
    channel?: number;
  }): Promise<NotificationDto[]> => {
    const response = await api.get<NotificationDto[]>(`${BASE}/notifications`, { params });
    return response.data;
  },

  createNotification: async (data: CreateNotification): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`${BASE}/notifications`, data);
    return response.data;
  },

  updateNotification: async (id: string, data: CreateNotification): Promise<void> => {
    await api.put(`${BASE}/notifications/${id}`, { id, ...data });
  },

  sendNotification: async (id: string): Promise<void> => {
    await api.post(`${BASE}/notifications/${id}/send`);
  },

  deleteNotification: async (id: string): Promise<void> => {
    await api.delete(`${BASE}/notifications/${id}`);
  },

  // ─── Audit Logs ────────────────────────────────────────────
  getAuditLogs: async (params?: {
    userId?: string;
    tenantId?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    searchTerm?: string;
  }): Promise<AuditLogDto[]> => {
    const response = await api.get<AuditLogDto[]>(`${BASE}/audit-logs`, { params });
    return response.data;
  },

  // ─── Security Monitoring ───────────────────────────────────
  getSecurityEvents: async (params?: {
    eventType?: string;
    tenantId?: string;
    isSuccess?: boolean;
    fromDate?: string;
    toDate?: string;
  }): Promise<SecurityEventDto[]> => {
    const response = await api.get<SecurityEventDto[]>(`${BASE}/security-events`, { params });
    return response.data;
  },

  // ─── Usage Monitoring ──────────────────────────────────────
  getUsageMonitoring: async (params?: {
    searchTerm?: string;
  }): Promise<UsageMonitoringDto[]> => {
    const response = await api.get<UsageMonitoringDto[]>(`${BASE}/usage-monitoring`, { params });
    return response.data;
  },

  // ─── System Settings ───────────────────────────────────────
  getSystemSettings: async (params?: {
    category?: string;
  }): Promise<SystemSettingsDto[]> => {
    const response = await api.get<SystemSettingsDto[]>(`${BASE}/system-settings`, { params });
    return response.data;
  },

  updateSystemSetting: async (id: string, data: UpdateSystemSetting): Promise<void> => {
    await api.put(`${BASE}/system-settings/${id}`, data);
  },
};
